import { FormEvent, useMemo, useState, type ElementType } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  FormControl,
  Switch,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Stack,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiCamera, FiLogOut } from 'react-icons/fi';
import AppLayout from '../components/AppLayout';
import Balances from '../components/Balances';
import Transactions from '../components/Transactions';
import EmptyState from '../components/EmptyState';
import useToasts from '../components/useToasts';
import QrScannerModal from '../components/QrScannerModal';

interface HomePageProps {
  onLogout: () => void;
  token: string | null;
  userEmail: string;
  userRole: string;
}

interface PlayerRecord {
  email?: string;
  cpf?: string;
  _gameAccount?: string;
  _gameAccountBalance?: string;
}

interface BalanceRecord {
  amount?: number;
  withdrawableAmount?: number;
}

interface TransactionRecord {
  _id?: string;
  id?: string;
  createdAt?: string;
  type?: string;
  method?: string;
  description?: string;
  amount?: number;
  status?: string;
  _transaction?: {
    status?: string;
  } | null;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value / 100);

const formatCurrencyInput = (value: string) => {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  const cents = Number.parseInt(digits, 10);
  return formatCurrency(Number.isNaN(cents) ? 0 : cents);
};

const parseCurrencyToCents = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits ? Number.parseInt(digits, 10) : 0;
};

const sanitizeCpf = (value: string) => value.replace(/\D/g, '').slice(0, 11);

const formatCpf = (value: string) => {
  const digits = sanitizeCpf(value);
  if (!digits) return '';
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 9);
  const part4 = digits.slice(9, 11);
  return [part1, part2, part3].filter(Boolean).join('.') + (part4 ? `-${part4}` : '');
};

const HomePage = ({ onLogout, token, userEmail, userRole }: HomePageProps) => {
  const navigate = useNavigate();
  const { showToast } = useToasts();

  const [cpfInput, setCpfInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [player, setPlayer] = useState<PlayerRecord | null>(null);
  const [balance, setBalance] = useState<BalanceRecord | null>(null);
  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [transactionsError, setTransactionsError] = useState('');
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const addModal = useDisclosure();
  const withdrawModal = useDisclosure();
  const tournamentModal = useDisclosure();
  const qrScannerModal = useDisclosure();
  const [isSmallScreen] = useMediaQuery('(max-width: 768px)');
  const isLiveOperator = userRole === 'LIVE_OPERATOR';

  const apiBaseUrl = useMemo(
    () => (process.env.NODE_ENV === 'development' ? '/api/v1' : 'https://newapi.chippix.com.br/v1'),
    []
  );

  const maskedCpf = formatCpf(cpfInput);

  const totalAmount = balance?.amount ?? 0;
  const withdrawableAmount = balance?.withdrawableAmount ?? 0;
  const lockedAmount = Math.max(totalAmount - withdrawableAmount, 0);
  const totalCardAmount = lockedAmount > 0 ? lockedAmount : totalAmount;

  const canManageBalance = Boolean(player?._gameAccountBalance);
  const canRegisterTournament = Boolean(player?._gameAccount);

  const handleCpfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = sanitizeCpf(event.target.value);
    setCpfInput(digits);
  };

  const handleLogoutClick = () => {
    onLogout();
    navigate('/');
  };

  const fetchBalance = async (balanceId: string | undefined | null) => {
    if (!balanceId) {
      setBalance(null);
      return null;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/game-account-balance/${balanceId}/club`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || 'Não foi possível carregar os saldos.');
      }
      setBalance(json?.data ?? null);
      return json?.data ?? null;
    } catch (error) {
      setBalance(null);
      showToast({
        title: 'Erro ao carregar saldo',
        description: error instanceof Error ? error.message : 'Erro inesperado ao buscar o saldo.',
        status: 'error',
      });
      return null;
    }
  };

  const fetchTransactions = async (gameAccountId: string | undefined | null) => {
    if (!gameAccountId) {
      setTransactions([]);
      return [];
    }
    setIsTransactionsLoading(true);
    setTransactionsError('');
    try {
      const response = await fetch(`${apiBaseUrl}/game-account/${gameAccountId}/live-transactions`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};
      if (!response.ok) {
        throw new Error(payload?.message || 'Não foi possível carregar as transações.');
      }
      const items = Array.isArray(payload?.data) ? payload.data : [];
      setTransactions(items);
      return items;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro inesperado ao carregar as transações.';
      setTransactionsError(message);
      setTransactions([]);
      return [];
    } finally {
      setIsTransactionsLoading(false);
    }
  };

  const fetchPlayerAndRelatedData = async (cpfDigits: string, showErrors = true) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/club/live-game-account?cpf=${encodeURIComponent(cpfDigits)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};
      if (!response.ok) {
        throw new Error(payload?.message || 'Não foi possível localizar o jogador.');
      }
      const playerRecord: PlayerRecord | null = payload?.data ?? null;
      setPlayer(playerRecord);

      if (!playerRecord?._gameAccount && showErrors) {
        setSearchError('Nenhum resultado encontrado para o CPF informado.');
      } else {
        setSearchError('');
      }

      await Promise.all([
        fetchBalance(playerRecord?._gameAccountBalance ?? null),
        fetchTransactions(playerRecord?._gameAccount ?? null),
      ]);
    } catch (error) {
      if (showErrors) {
        setSearchError(error instanceof Error ? error.message : 'Erro inesperado ao buscar informações do jogador.');
      }
      setPlayer(null);
      setBalance(null);
      setTransactions([]);
    }
  };

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const digits = sanitizeCpf(cpfInput);
    if (digits.length !== 11) {
      setSearchError('Informe um CPF válido com 11 dígitos.');
      return;
    }

    setIsSearching(true);
    await fetchPlayerAndRelatedData(digits, true);
    setIsSearching(false);
  };

  const handleRefresh = async () => {
    if (!player?._gameAccount || cpfInput.length !== 11) {
      setSearchError('Busque um CPF válido antes de atualizar.');
      return;
    }
    setIsRefreshing(true);
    await fetchPlayerAndRelatedData(cpfInput, false);
    setIsRefreshing(false);
    showToast({
      title: 'Dados atualizados',
      description: 'As informações da carteira foram sincronizadas com sucesso.',
      status: 'success',
    });
  };

  const handleDepositSuccess = async (message: string) => {
    addModal.onClose();
    await handleRefresh();
    showToast({
      title: 'Saldo adicionado',
      description: message,
      status: 'success',
    });
  };

  const handleWithdrawSuccess = async (message: string) => {
    withdrawModal.onClose();
    await handleRefresh();
    showToast({
      title: 'Retirada registrada',
      description: message,
      status: 'success',
    });
  };

  const handleTournamentSuccess = async (message: string) => {
    tournamentModal.onClose();
    await handleRefresh();
    showToast({
      title: 'Inscrição concluída',
      description: message,
      status: 'success',
    });
  };

  const searchCard = (
    <Skeleton isLoaded={!isSearching} rounded="lg">
      <Box
        borderWidth="1px"
        borderColor="gray.200"
        _dark={{ borderColor: 'gray.700', bg: 'gray.800' }}
        bg="white"
        rounded="lg"
        p={{ base: 6, md: 8 }}
        boxShadow="sm"
      >
        <Box as="form" onSubmit={handleSearch}>
          <Stack spacing={6}>
            <FormControl>
            <FormLabel fontWeight="medium">CPF do jogador</FormLabel>
            <Input
              size="lg"
              placeholder="000.000.000-00"
              value={maskedCpf}
              onChange={handleCpfChange}
              inputMode="numeric"
              maxLength={14}
            />
            <FormHelperText color="text.600">Digite o CPF completo para localizar a carteira.</FormHelperText>
          </FormControl>
          {searchError && (
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <AlertDescription>{searchError}</AlertDescription>
            </Alert>
          )}
            <Button
              type="submit"
              size="lg"
              colorScheme="brand"
              isLoading={isSearching}
            >
              Buscar carteira
            </Button>
          </Stack>
        </Box>
      </Box>
    </Skeleton>
  );

  const content = player ? (
    <Stack spacing={10}>
      <Balances
        isLoading={isSearching || isRefreshing}
        isEmpty={!balance}
        total={totalCardAmount}
        withdrawable={withdrawableAmount}
        locked={lockedAmount}
      />
      <Box>
        <Transactions
          isLoading={isTransactionsLoading}
          error={transactionsError}
          data={transactions}
        />
      </Box>
    </Stack>
  ) : (
    <EmptyState
      title="Busque um jogador para começar"
      description="Utilize o CPF para encontrar a carteira do jogador e visualizar saldos, movimentações e ações disponíveis."
    />
  );

  const actions = (
    <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align={{ base: 'stretch', sm: 'center' }}>
      <Button
        colorScheme="brand"
        leftIcon={<Icon as={FiCamera as ElementType} />}
        onClick={qrScannerModal.onOpen}
      >
        Validar inscrição
      </Button>
      {!isLiveOperator && (
        <Button
          variant="ghost"
          onClick={handleRefresh}
          isLoading={isRefreshing}
          isDisabled={!player}
        >
          Atualizar
        </Button>
      )}
      <Button
        variant="ghost"
        leftIcon={<Icon as={FiLogOut as ElementType} />}
        onClick={handleLogoutClick}
      >
        Sair
      </Button>
    </Stack>
  );

  return (
    <AppLayout
      title="Carteira do jogador"
      subtitle={player ? `Gerencie o saldo de ${player.email || 'jogador'} com ações rápidas e seguras.` : 'Busque um CPF para acessar e gerenciar a carteira do jogador com rapidez.'}
      actions={actions}
    >
      <Stack spacing={12}>
        {isLiveOperator ? (
          <Alert status="info" borderRadius="lg" variant="subtle">
            <AlertIcon />
            <Box>
              <AlertTitle>Modo operador</AlertTitle>
              <AlertDescription>
                Este usuário pode apenas validar inscrições via QR Code. Use o botão acima para abrir a câmera e validar novas entradas.
              </AlertDescription>
            </Box>
          </Alert>
        ) : (
          <>
            {searchCard}
            {player && (
              <Stack spacing={6} direction={{ base: 'column', md: 'row' }} justify="flex-start">
                <Button colorScheme="brand" size={isSmallScreen ? 'lg' : 'md'} onClick={addModal.onOpen} isDisabled={!canManageBalance}>
                  Adicionar saldo
                </Button>
                <Button variant="subtle" colorScheme="brand" size={isSmallScreen ? 'lg' : 'md'} onClick={withdrawModal.onOpen} isDisabled={!canManageBalance}>
                  Solicitar saque
                </Button>
                <Button variant="ghost" size={isSmallScreen ? 'lg' : 'md'} onClick={tournamentModal.onOpen} isDisabled={!canRegisterTournament}>
                  Inscrever no torneio
                </Button>
              </Stack>
            )}
            {content}
          </>
        )}
        {token && (
          <Alert status="info" borderRadius="lg" variant="left-accent">
            <AlertIcon />
            <Box>
              <AlertTitle>Sessão ativa</AlertTitle>
              <AlertDescription>
                O token atual está disponível para outras requisições protegidas. Pré-visualização: <Box as="span" fontFamily="mono">{token.slice(0, 12)}...</Box>
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </Stack>

      <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adicionar saldo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {canManageBalance ? (
              <AddBalanceForm
                balanceId={player?._gameAccountBalance ?? ''}
                apiBaseUrl={apiBaseUrl}
                token={token}
                onSuccess={handleDepositSuccess}
              />
            ) : (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <AlertDescription>Conta de saldo não disponível para este jogador.</AlertDescription>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={addModal.onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={withdrawModal.isOpen} onClose={withdrawModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Retirar saldo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {canManageBalance ? (
              <WithdrawBalanceForm
                balanceId={player?._gameAccountBalance ?? ''}
                apiBaseUrl={apiBaseUrl}
                token={token}
                onSuccess={handleWithdrawSuccess}
              />
            ) : (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <AlertDescription>Conta de saldo não disponível para este jogador.</AlertDescription>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={withdrawModal.onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={tournamentModal.isOpen} onClose={tournamentModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Inscrever no torneio</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {canRegisterTournament ? (
              <TournamentRegistrationForm
                gameAccountId={player?._gameAccount ?? ''}
                apiBaseUrl={apiBaseUrl}
                token={token}
                onSuccess={handleTournamentSuccess}
              />
            ) : (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <AlertDescription>Conta de jogo não disponível para este jogador.</AlertDescription>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={tournamentModal.onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <QrScannerModal
        isOpen={qrScannerModal.isOpen}
        onClose={qrScannerModal.onClose}
        apiBaseUrl={apiBaseUrl}
        token={token}
      />
    </AppLayout>
  );
};

interface FormBaseProps {
  apiBaseUrl: string;
  token: string | null;
  onSuccess: (message: string) => Promise<void> | void;
}

interface AddBalanceFormProps extends FormBaseProps {
  balanceId: string;
}

const AddBalanceForm = ({ balanceId, apiBaseUrl, token, onSuccess }: AddBalanceFormProps) => {
  const [amountInput, setAmountInput] = useState('');
  const [description, setDescription] = useState('');
  const [trackingSheetId, setTrackingSheetId] = useState('');
  const [eventMethod, setEventMethod] = useState('EVENT_CASH');
  const [isWithdrawable, setIsWithdrawable] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { showToast } = useToasts();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');

    const amount = parseCurrencyToCents(amountInput);
    if (amount <= 0) {
      setErrorMessage('Informe um valor maior que zero.');
      return;
    }
    if (!description.trim()) {
      setErrorMessage('Descreva o motivo do crédito.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${apiBaseUrl}/game-account-balance/${balanceId}/deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          amount,
          description: description.trim(),
          eventMethod,
          isWithdrawable,
          ...(trackingSheetId.trim() && { trackingSheetId: trackingSheetId.trim() }),
        }),
      });

      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};

      if (!response.ok) {
        throw new Error(payload?.message || 'Não foi possível adicionar o saldo.');
      }

      setAmountInput('');
      setDescription('');
      setTrackingSheetId('');
      setEventMethod('EVENT_CASH');
      setIsWithdrawable(true);

      const successMessage =
        typeof payload === 'string'
          ? payload
          : payload?.message || 'Saldo adicionado com sucesso.';

      await onSuccess(successMessage);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro inesperado ao adicionar o saldo.';
      setErrorMessage(message);
      showToast({
        title: 'Falha ao adicionar saldo',
        description: message,
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit} display="grid" gap={4}>
      {errorMessage && (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <FormControl isRequired>
        <FormLabel>Valor (R$)</FormLabel>
        <Input
          value={amountInput}
          placeholder="0,00"
          onChange={(event) => setAmountInput(formatCurrencyInput(event.target.value))}
          inputMode="decimal"
        />
        <FormHelperText>Informe o valor em reais; o envio será convertido para centavos.</FormHelperText>
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Descrição</FormLabel>
        <Input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Ex.: Crédito manual"
        />
      </FormControl>
      <FormControl>
        <FormLabel>ID da planilha de controle</FormLabel>
        <Input
          value={trackingSheetId}
          onChange={(event) => setTrackingSheetId(event.target.value)}
          placeholder="Opcional"
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Método de pagamento</FormLabel>
        <Select value={eventMethod} onChange={(event) => setEventMethod(event.target.value)}>
          <option value="EVENT_CASH">Dinheiro</option>
          <option value="EVENT_CREDITCARD">Cartão de crédito</option>
          <option value="EVENT_DEBIT">Cartão de débito</option>
          <option value="EVENT_CREDIT">Crédito interno</option>
        </Select>
      </FormControl>
      <FormControl display="flex" alignItems="center">
        <Switch
          id="balance-withdrawable"
          isChecked={isWithdrawable}
          onChange={(event) => setIsWithdrawable(event.target.checked)}
          colorScheme="brand"
        />
        <FormLabel htmlFor="balance-withdrawable" mb="0" ml={3}>
          Disponível para saque
        </FormLabel>
      </FormControl>
      <Button type="submit" colorScheme="brand" isLoading={isSubmitting}>
        Adicionar saldo
      </Button>
    </Box>
  );
};

interface WithdrawBalanceFormProps extends FormBaseProps {
  balanceId: string;
}

const WithdrawBalanceForm = ({ balanceId, apiBaseUrl, token, onSuccess }: WithdrawBalanceFormProps) => {
  const [amountInput, setAmountInput] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { showToast } = useToasts();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');

    const amount = parseCurrencyToCents(amountInput);
    if (amount <= 0) {
      setErrorMessage('Informe um valor maior que zero.');
      return;
    }
    if (!description.trim()) {
      setErrorMessage('Descreva o motivo da retirada.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${apiBaseUrl}/game-account-balance/${balanceId}/withdraw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          amount,
          description: description.trim(),
          eventMethod: 'EVENT_WITHDRAW',
        }),
      });

      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};

      if (!response.ok) {
        throw new Error(payload?.message || 'Não foi possível registrar a retirada.');
      }

      setAmountInput('');
      setDescription('');

      const successMessage =
        typeof payload === 'string'
          ? payload
          : payload?.message || 'Retirada registrada com sucesso.';

      await onSuccess(successMessage);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro inesperado ao retirar saldo.';
      setErrorMessage(message);
      showToast({
        title: 'Falha ao retirar saldo',
        description: message,
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit} display="grid" gap={4}>
      {errorMessage && (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <FormControl isRequired>
        <FormLabel>Valor (R$)</FormLabel>
        <Input
          value={amountInput}
          onChange={(event) => setAmountInput(formatCurrencyInput(event.target.value))}
          placeholder="0,00"
          inputMode="decimal"
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Descrição</FormLabel>
        <Input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Ex.: Pagamento de premiação"
        />
      </FormControl>
      <Button type="submit" colorScheme="brand" isLoading={isSubmitting}>
        Registrar retirada
      </Button>
    </Box>
  );
};

interface TournamentRegistrationFormProps extends FormBaseProps {
  gameAccountId: string;
}

const TournamentRegistrationForm = ({
  gameAccountId,
  apiBaseUrl,
  token,
  onSuccess,
}: TournamentRegistrationFormProps) => {
  const [amountInput, setAmountInput] = useState('');
  const [feeInput, setFeeInput] = useState('');
  const [description, setDescription] = useState('');
  const [eventMethod, setEventMethod] = useState('EVENT_CREDITCARD');
  const [isWithdrawable, setIsWithdrawable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { showToast } = useToasts();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');

    const amount = parseCurrencyToCents(amountInput);
    const feeRegistration = parseCurrencyToCents(feeInput);

    if (amount <= 0) {
      setErrorMessage('Informe o valor total pago pelo jogador.');
      return;
    }
    if (feeRegistration <= 0) {
      setErrorMessage('Informe o valor do torneio.');
      return;
    }
    if (!description.trim()) {
      setErrorMessage('Descreva o motivo da inscrição.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${apiBaseUrl}/game-account/${gameAccountId}/tournament-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          amount,
          feeRegistration,
          description: description.trim(),
          eventMethod,
          isWithdrawable,
        }),
      });

      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};

      if (!response.ok) {
        throw new Error(payload?.message || 'Não foi possível registrar a inscrição.');
      }

      setAmountInput('');
      setFeeInput('');
      setDescription('');
      setEventMethod('EVENT_CREDITCARD');
      setIsWithdrawable(false);

      const successMessage =
        typeof payload === 'string'
          ? payload
          : payload?.message || 'Inscrição registrada com sucesso.';

      await onSuccess(successMessage);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro inesperado ao registrar a inscrição.';
      setErrorMessage(message);
      showToast({
        title: 'Falha ao registrar inscrição',
        description: message,
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit} display="grid" gap={4}>
      {errorMessage && (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <FormControl isRequired>
        <FormLabel>Valor total (R$)</FormLabel>
        <Input
          value={amountInput}
          onChange={(event) => setAmountInput(formatCurrencyInput(event.target.value))}
          placeholder="0,00"
          inputMode="decimal"
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Valor do torneio (R$)</FormLabel>
        <Input
          value={feeInput}
          onChange={(event) => setFeeInput(formatCurrencyInput(event.target.value))}
          placeholder="0,00"
          inputMode="decimal"
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Descrição</FormLabel>
        <Input
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
            if (errorMessage) {
              setErrorMessage('');
            }
          }}
          placeholder="Ex.: Pagamento BUY-IN torneio XPTO"
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Método de pagamento</FormLabel>
        <Select value={eventMethod} onChange={(event) => setEventMethod(event.target.value)}>
          <option value="EVENT_CASH">Dinheiro</option>
          <option value="EVENT_CREDITCARD">Cartão de crédito</option>
          <option value="EVENT_DEBIT">Cartão de débito</option>
          <option value="EVENT_CREDIT">Crédito interno</option>
        </Select>
      </FormControl>
      <FormControl display="flex" alignItems="center">
        <Switch
          id="tournament-withdrawable"
          isChecked={isWithdrawable}
          onChange={(event) => setIsWithdrawable(event.target.checked)}
          colorScheme="brand"
        />
        <FormLabel htmlFor="tournament-withdrawable" mb="0" ml={3}>
          Disponível para saque
        </FormLabel>
      </FormControl>
      <Button type="submit" colorScheme="brand" isLoading={isSubmitting}>
        Registrar inscrição
      </Button>
    </Box>
  );
};

export default HomePage;
